<template>
  <div
    class="company-card"
    @click="goToCompany"
  >

    <!-- LOGO -->

    <div class="logo-shell">

      <img
        v-if="company?.logo_url && !logoFailed"
        :src="company.logo_url"
        class="logo"
        alt="Company Logo"
        @error="handleLogoError"
      />

      <div
        v-else
        class="logo-placeholder"
      >
        <span class="logo-placeholder-letter">
          {{ companyInitial }}
        </span>
      </div>

    </div>


    <!-- INFO -->

    <div class="info">

      <div class="top-row">

        <h2 class="name">
          {{ company?.name || "Без названия" }}
        </h2>

        <span class="badge">
          <span class="badge-dot"></span>
          Вы тут работаете
        </span>

      </div>


      <div class="location">

        <svg
          viewBox="0 0 20 20"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M10 17C10 17 15 12.5 15 8.5C15 5.7 12.8 3.5 10 3.5C7.2 3.5 5 5.7 5 8.5C5 12.5 10 17 10 17Z"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-linejoin="round"
          />

          <circle
            cx="10"
            cy="8.5"
            r="1.7"
            stroke="currentColor"
            stroke-width="1.5"
          />
        </svg>

        <span>
          {{ locationText || "Локация не указана" }}
        </span>

      </div>


      <p class="description">
        {{ company?.about_company || placeholder }}
      </p>

    </div>


    <!-- DELETE -->

    <button
      type="button"
      class="close-btn"
      aria-label="Удалить компанию"
      title="Удалить компанию"
      @click.stop="$emit('delete')"
    >
      <svg
        viewBox="0 0 20 20"
        fill="none"
        aria-hidden="true"
      >
        <path
          d="M6.5 7V14"
          stroke="currentColor"
          stroke-width="1.4"
          stroke-linecap="round"
        />

        <path
          d="M10 7V14"
          stroke="currentColor"
          stroke-width="1.4"
          stroke-linecap="round"
        />

        <path
          d="M13.5 7V14"
          stroke="currentColor"
          stroke-width="1.4"
          stroke-linecap="round"
        />

        <path
          d="M4.8 5H15.2"
          stroke="currentColor"
          stroke-width="1.4"
          stroke-linecap="round"
        />

        <path
          d="M7.7 5V3.8H12.3V5"
          stroke="currentColor"
          stroke-width="1.4"
          stroke-linejoin="round"
        />

        <path
          d="M5.8 5L6.4 16H13.6L14.2 5"
          stroke="currentColor"
          stroke-width="1.4"
          stroke-linejoin="round"
        />
      </svg>
    </button>

  </div>
</template>


<script setup>

import {
  computed,
  ref
} from "vue";

import RegionService
  from "../../utils/RegionService";

import {
  useRouter
} from "vue-router";


const router =
  useRouter();


const props =
  defineProps({

    company: {
      type: Object,
      required: true
    }

  });


const placeholder =
  "Описание компании ещё не заполнено";


const logoFailed =
  ref(false);


const locationText =
  computed(() => {

    return RegionService.handleRegion(
      props.company?.region_id
    );

  });


const companyInitial =
  computed(() => {

    const name =
      props.company?.name?.trim();

    if (!name) {
      return "C";
    }

    return name
      .charAt(0)
      .toUpperCase();

  });


function handleLogoError() {

  logoFailed.value =
    true;

}


function goToCompany() {

  if (
    !props.company?.company_id &&
    !props.company?.id
  ) {

    console.warn(
      "❗ Нет ID у компании:",
      props.company
    );

    return;

  }


  const id =
    props.company.company_id ||
    props.company.id;


  router.push(
    `/edit-company/${id}`
  );

}

</script>


<style scoped>

/* ============================================================
   CARD
============================================================ */

.company-card {
  width: 100%;

  box-sizing: border-box;

  position: relative;

  display: flex;
  align-items: flex-start;

  gap: 14px;

  padding:
    16px;

  color:
    #30353c;

  background:
    rgba(255, 255, 255, 0.96);

  border:
    1px solid
    rgba(25, 40, 60, 0.065);

  border-radius:
    20px;

  box-shadow:
    0
    10px
    28px
    rgba(15, 23, 42, 0.045);

  cursor:
    pointer;

  transition:
    transform .18s ease,
    border-color .18s ease,
    box-shadow .18s ease,
    background-color .18s ease;
}


.company-card:hover {
  transform:
    translateY(-1px);

  border-color:
    rgba(55, 127, 228, 0.10);

  box-shadow:
    0
    14px
    32px
    rgba(15, 23, 42, 0.065);
}


.company-card:active {
  transform:
    scale(.992);
}


/* ============================================================
   LOGO SHELL
============================================================ */

.logo-shell {
  width: 64px;
  height: 64px;

  flex:
    0
    0
    64px;

  display: flex;
  align-items: center;
  justify-content: center;

  box-sizing: border-box;

  padding: 3px;

  background:
    linear-gradient(
      145deg,
      #f5f7f9,
      #edf0f3
    );

  border:
    1px solid
    rgba(30, 45, 65, 0.05);

  border-radius:
    17px;

  box-shadow:
    0
    6px
    15px
    rgba(15, 23, 42, 0.04);
}


/* ============================================================
   LOGO
============================================================ */

.logo {
  width: 58px;
  height: 58px;

  display: block;

  object-fit:
    cover;

  border-radius:
    14px;
}


/* ============================================================
   LOGO PLACEHOLDER
============================================================ */

.logo-placeholder {
  width: 58px;
  height: 58px;

  display: flex;
  align-items: center;
  justify-content: center;

  box-sizing: border-box;

  overflow: hidden;

  background:
    radial-gradient(
      circle at 30% 25%,
      rgba(110, 165, 235, 0.18),
      transparent 48%
    ),
    linear-gradient(
      145deg,
      #eef3f8,
      #e3eaf2
    );

  border:
    1px solid
    rgba(65, 110, 170, 0.08);

  border-radius:
    14px;
}


.logo-placeholder-letter {
  color:
    #5d7fa8;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size:
    22px;

  line-height:
    1;

  letter-spacing:
    -0.6px;

  text-transform:
    uppercase;
}


/* ============================================================
   INFO
============================================================ */

.info {
  min-width: 0;

  flex: 1;

  padding-right:
    30px;
}


/* ============================================================
   TOP
============================================================ */

.top-row {
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  gap: 7px;
}


.name {
  max-width: 100%;

  margin: 0;

  overflow: hidden;

  color:
    #30353c;

  font-family:
    "Eastman-Bold",
    sans-serif;

  font-size:
    15px;

  line-height:
    1.25;

  letter-spacing:
    -0.25px;

  white-space:
    nowrap;

  text-overflow:
    ellipsis;
}


/* ============================================================
   BADGE
============================================================ */

.badge {
  min-height: 23px;

  display: inline-flex;
  align-items: center;

  gap: 6px;

  box-sizing: border-box;

  padding:
    0
    9px;

  color:
    #4f78aa;

  background:
    rgba(55, 127, 228, 0.075);

  border:
    1px solid
    rgba(55, 127, 228, 0.08);

  border-radius:
    8px;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size:
    9px;
}


.badge-dot {
  width: 5px;
  height: 5px;

  flex:
    0
    0
    5px;

  background:
    #4d9b78;

  border-radius:
    50%;

  box-shadow:
    0
    0
    0
    3px
    rgba(77, 155, 120, 0.08);
}


/* ============================================================
   LOCATION
============================================================ */

.location {
  margin-top:
    10px;

  display: flex;
  align-items: center;

  gap: 6px;

  color:
    #8a929c;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size:
    10px;

  line-height:
    1.35;
}


.location svg {
  width: 14px;
  height: 14px;

  flex:
    0
    0
    14px;

  color:
    #7894b8;
}


/* ============================================================
   DESCRIPTION
============================================================ */

.description {
  margin:
    9px
    0
    0;

  overflow: hidden;

  color:
    #7d858e;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size:
    11px;

  line-height:
    1.45;

  display:
    -webkit-box;

  -webkit-line-clamp:
    3;

  -webkit-box-orient:
    vertical;
}


/* ============================================================
   DELETE
============================================================ */

.close-btn {
  position: absolute;

  top:
    12px;

  right:
    12px;

  width:
    29px;

  height:
    29px;

  display: flex;
  align-items: center;
  justify-content: center;

  padding: 0;

  color:
    #a0a6ad;

  background:
    rgba(35, 50, 70, 0.035);

  border:
    1px solid
    rgba(35, 50, 70, 0.045);

  border-radius:
    9px;

  cursor:
    pointer;

  opacity:
    .8;

  transition:
    color .18s ease,
    background-color .18s ease,
    border-color .18s ease,
    opacity .18s ease,
    transform .18s ease;
}


.close-btn svg {
  width:
    14px;

  height:
    14px;
}


.close-btn:hover {
  color:
    #c66666;

  background:
    rgba(190, 80, 80, 0.07);

  border-color:
    rgba(190, 80, 80, 0.08);

  opacity:
    1;

  transform:
    scale(1.03);
}


.close-btn:active {
  transform:
    scale(.94);
}


/* ============================================================
   DARK
============================================================ */

html[data-theme="dark"]
.company-card {
  color:
    #e8eaed;

  background:
    linear-gradient(
      145deg,
      #22242a,
      #1d1f24
    );

  border-color:
    rgba(255, 255, 255, 0.065);

  box-shadow:
    0
    14px
    32px
    rgba(0, 0, 0, 0.16);
}


html[data-theme="dark"]
.company-card:hover {
  border-color:
    rgba(110, 155, 215, 0.10);

  box-shadow:
    0
    18px
    36px
    rgba(0, 0, 0, 0.22);
}


html[data-theme="dark"]
.logo-shell {
  background:
    linear-gradient(
      145deg,
      #2a2d33,
      #25272c
    );

  border-color:
    rgba(255, 255, 255, 0.055);

  box-shadow:
    0
    7px
    18px
    rgba(0, 0, 0, 0.18);
}


html[data-theme="dark"]
.logo-placeholder {
  background:
    radial-gradient(
      circle at 30% 25%,
      rgba(91, 158, 255, 0.12),
      transparent 50%
    ),
    linear-gradient(
      145deg,
      #292d34,
      #24272d
    );

  border-color:
    rgba(255, 255, 255, 0.055);
}


html[data-theme="dark"]
.logo-placeholder-letter {
  color:
    #8fb4e8;
}


html[data-theme="dark"]
.name {
  color:
    #f0f1f3;
}


html[data-theme="dark"]
.badge {
  color:
    #a8c6ec;

  background:
    rgba(91, 158, 255, 0.075);

  border-color:
    rgba(91, 158, 255, 0.085);
}


html[data-theme="dark"]
.location {
  color:
    #7f8790;
}


html[data-theme="dark"]
.location svg {
  color:
    #829bbb;
}


html[data-theme="dark"]
.description {
  color:
    #858b94;
}


html[data-theme="dark"]
.close-btn {
  color:
    #707780;

  background:
    rgba(255, 255, 255, 0.025);

  border-color:
    rgba(255, 255, 255, 0.04);
}


html[data-theme="dark"]
.close-btn:hover {
  color:
    #d18a8a;

  background:
    rgba(195, 85, 85, 0.07);

  border-color:
    rgba(195, 85, 85, 0.08);
}


/* ============================================================
   MOBILE
============================================================ */

@media (max-width: 360px) {

  .company-card {
    padding:
      14px;

    gap:
      12px;

    border-radius:
      18px;
  }


  .logo-shell {
    width:
      58px;

    height:
      58px;

    flex-basis:
      58px;

    border-radius:
      15px;
  }


  .logo,
  .logo-placeholder {
    width:
      52px;

    height:
      52px;

    border-radius:
      12px;
  }


  .logo-placeholder-letter {
    font-size:
      20px;
  }


  .name {
    font-size:
      14px;
  }


  .description {
    font-size:
      10px;
  }


  .location {
    font-size:
      9px;
  }

}


/* ============================================================
   REDUCED MOTION
============================================================ */

@media (prefers-reduced-motion: reduce) {

  .company-card,
  .close-btn {
    transition:
      none;
  }

}

</style>